import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime } from 'rxjs';

import {
  SearchableListBoxConfig,
  SearchableListItemType,
} from '@shared/components/searchable-listbox/searchable-listbox.model';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules';
import { SelectableListboxComponent } from '@shared/components/selectable-listbox/selectable-listbox.component';

@Component({
  selector: 'cipo-searchable-listbox',
  templateUrl: './searchable-listbox.component.html',
  styleUrls: ['./searchable-listbox.component.scss'],
  imports: [TranslateModule, CipoFormModule, NgxSkeletonLoaderModule, SelectableListboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchableListboxComponent<TKey = number> {
  values = model<TKey[]>();
  options = input<SearchableListItemType<TKey>[]>();
  config = input<SearchableListBoxConfig, SearchableListBoxConfig>(null, {
    transform: config => {
      this.skeletonColumns.set(Array(config.columns || 1));
      return config;
    },
  });

  skeletonColumns = signal([]);
  skeleton = signal(Array(11));
  loading = signal<boolean>(false);
  searchControl = new FormControl('');
  searchValue = signal('');
  filteredOptions = computed(() => {
    const searchValue = this.searchValue();
    return this.options()?.filter(o => o.value?.toLowerCase()?.includes(searchValue?.toLowerCase()));
  });

  constructor() {
    effect(() => {
      const options = this.options();
      if (!options) {
        this.loading.set(true);
        return;
      }
      this.loading.set(false);
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.short))
      .subscribe(val => this.searchValue.set(val));
  }

  clearInput() {
    this.searchControl.setValue('');
  }
}
